import { useFeatureFlagEnabled } from '@posthog/react'

import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import { applyCouponPercentage } from '~/campaigns/utils/helper'
import { getPricePageMonthlyPromo, getPricePageYearlyPromo } from '~/get-started/us-onboarding/consts/pricePromo.consts'
import { BACK_TO_SCHOOL_2026_FLAG } from '~/shared/constants/backToSchoolPromo.const'
import { useGetStripePriceQuery } from '~/subscription/apis/subscription.api'
import { Coupon } from '~/subscription/types/subscription.types'
import { transformPrice } from '~/subscription/util'

type PromoInterval = 'monthly' | 'yearly'

interface PricePagePromotion {
  promoCode?: string
  coupon?: Coupon
  isPromoActive: boolean
  promoPercentage?: number
  applyPromoDiscount: (total: number) => number
}

export default function usePricePagePromotion({
  currency,
  priceId,
  interval = 'yearly',
  partnerDetails,
}: {
  currency: string
  priceId?: string
  interval?: PromoInterval
  /** The funnel's partner; one that keeps the promo gets it in USD with the flag off. */
  partnerDetails?: PartnerDetailsType
}): PricePagePromotion {
  // With the flag off there is no promo: the lookup is skipped and every value falls back to full price.
  const partnerKeepsPromo = !!partnerDetails?.keepsBackToSchool2026 && currency?.toUpperCase() === 'USD'
  const flagEnabled = !!useFeatureFlagEnabled(BACK_TO_SCHOOL_2026_FLAG) || partnerKeepsPromo
  const getPromo = interval === 'monthly' ? getPricePageMonthlyPromo : getPricePageYearlyPromo
  const promo = flagEnabled ? getPromo(currency) : undefined
  const { data } = useGetStripePriceQuery(
    { currency, priceId, promotionId: promo?.promotionId },
    { skip: !priceId || !promo?.promotionId },
  )

  const coupon = data?.couponDetails
  const isPromoActive = Boolean(coupon?.percent_off || coupon?.amount_off)
  const promoPercentage = isPromoActive && coupon?.percent_off ? Math.floor(coupon.percent_off) : undefined

  const applyPromoDiscount = (total: number): number => {
    if (!isPromoActive || !coupon) return total
    if (coupon.percent_off) return applyCouponPercentage(total, coupon.percent_off)
    return Math.max(0, total - transformPrice(coupon.amount_off || 0, currency))
  }

  return { promoCode: promo?.code, coupon, isPromoActive, promoPercentage, applyPromoDiscount }
}
